/**
 * autofiller.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { ClassProvider, ErrorHandler, Injectable } from "@angular/core";
import { Event as RouterEvent } from "@angular/router";
import * as moment from "moment-timezone";

import { UserService } from "../../../auth/identity/user.service";
import { DataType } from "../../../common/data/data-types";
import { AutofillSource } from "../../../common/data/presentation-hints";
import { CONSOLE_HISTORY } from "../../../debug/console-interceptor";
import { GlobalErrorHandler } from "../../../debug/global-error-handler";
import { NavigationHistoryService }
    from "../../../debug/navigation-history.service";
import { Message } from "../../../server/messages/index";
import { MessagingService } from "../../../server/messaging.service";
import { jsonify, valueless } from "../../../support/utilities/functions";

import { AlgorithmStep, IntakeStep } from "../../structure/";
import { StepTransformer, TransformerEffects } from "./step-transformer";

/**
 * The {@link Autofiller} stage of the {@link AlgorithmStep} pipeline acts upon
 * steps with an "autofill" hint and provides them a value if they are empty,
 * from a source defined in the hint parameter.
 */
@Injectable()
export class Autofiller extends StepTransformer
{
    // Where this class should be sorted in the transformer list
    public order : number = 30;

    /**
     * Create the {@link Autofiller}
     */
    public constructor (
        protected messaging : MessagingService,
        protected navHistory : NavigationHistoryService,
        protected handler : ErrorHandler,
        protected users : UserService
    )
    {
        super();
    }

    public matchesType (type : any) : boolean
    {
        return type === IntakeStep;
    }

    /**
     * Operate on an {@link AlgorithmStep} and autofill it as necessary.
     */
    public apply (step : IntakeStep) : TransformerEffects
    {
        const flags : TransformerEffects = {};
        if (step.hints && step.hints.autofill)
        {
            // Verify the step's config matches what the autofill kind expects
            if (this.validateDataShape(step, step.hints.autofill))
            {
                // Some autofills are allowed to change the effective type of
                // the data from "string" to its (subtype) "json". This allows
                // the form to use a more appropriate input/display for json-y
                // data.
                if (this.castToJSON(step.hints.autofill))
                {
                    flags.former_data_type = step.type;
                    step.type = "json";
                }

                if (step.hints.autofillIteratively)
                {
                    // Application of scalar autofills to vectors
                    if (Array.isArray(step.value))
                    {
                        // Loop through and replace blanks
                        step.value.forEach((v : any, i : number) => {
                            if (valueless(v))
                            {
                                step.value[i] =
                                    this.getData(step.hints.autofill);
                                flags.autofilled = true;
                                flags.autofillTarget = step;
                            }
                        });
                    }
                    else if (valueless(step.value)
                            && step.hints.editable !== false)
                    {
                        // Initiate the list with a single entry
                        step.value = [this.getData(step.hints.autofill)];
                        flags.autofilled = true;
                        flags.autofillTarget = step;
                    }
                }
                else
                {
                    // Standard application
                    // If it has no value, autofill it!
                    if (valueless(step.value))
                    {
                        step.value = this.getData(step.hints.autofill);
                        flags.autofilled = true;
                        flags.autofillTarget = step;
                    }
                }
            }
        }
        return flags;
    }

    public create (step : IntakeStep) : TransformerEffects
    {
        return this.apply(step);
    }

    /**
     * Verify that the {@link IntakeStep}'s type, cardinality, and other
     * configuration match what is required by a given {@link AutofillSource}
     */
    private validateDataShape (step : IntakeStep, source : AutofillSource)
        : boolean
    {
        const expectedTypes : DataType[] = this.expectedDataTypes(source);
        if (expectedTypes.indexOf(step.type) === -1)
        {
            console.error(
                `Autofill ${source} expected different types:`,
                expectedTypes,
                step);
            return false;
        }

        const expectedCardinality : number = this.expectedCardinality(source);
        if (step.cardinality !== expectedCardinality)
        {
            if (expectedCardinality === 1
                && this.canIterateScalar(source))
            {
                step.hints.autofillOnExtend = true;
                step.hints.autofillIteratively = true;
                return true;
            }
            else
            {
                console.error(
                    `Autofill ${source} expected different cardinality:`,
                    expectedCardinality,
                    step);
                return false;
            }
        }

        return true;
    }

    /**
     * Identify the {@link DataType}s an {@link AutofillSource} operates on.
     */
    private expectedDataTypes (source : AutofillSource) : DataType[]
    {
        switch (source)
        {
            case "now":
            case "today":
                return ["date"];
            case "UTC-offset":
                return ["integer"];
            case "screen-size":
            case "window-size":
                return ["whole number"];
            case "pixel-ratio":
            case "geolocation":
                return ["float"];
            default:
                if (this.castToJSON(source)) {
                    return ["json", "string"];
                }
                return ["string"];
        }
    }

    /**
     * Identify the expected dimensionality of an {@link AutofillSource}.
     */
    private expectedCardinality (source : AutofillSource) : number
    {
        switch (source)
        {
            case "geolocation":
                return 2;
            case "screen-size": // TODO: move to 2
            case "window-size": // TODO: move to 2
            case "console-history":
            case "message-history":
            case "navigation-history":
                return 0;
            default:
                return 1;
        }
    }

    /**
     * Identify if the {@link AutofillSource} should narrow from string to json.
     */
    private castToJSON (source : AutofillSource) : boolean
    {
        return [
            "console-history",
            "stack-trace",
            "message-history",
            "navigation-history"
        ].indexOf(source) > -1;
    }

    /**
     * Whether a scalar autofill can be applied to a list or set state variable
     * by application to all of the blank fields.
     */
    private canIterateScalar (source : AutofillSource) : boolean
    {
        return [
            "today",
            "now",
            "user-name",
            "organization"
        ].indexOf(source) > -1;
    }

    /**
     * Retrieve data from an {@link AutofillSource}.
     */
    // tslint:disable-next-line:cyclomatic-complexity
    private getData (source : AutofillSource) : any
    {
        switch (source)
        {
            case "console-history":
                // Recent warnings, errors, & tables in the log
                return CONSOLE_HISTORY.filter(
                    ([e, t, a] : [string, any, any]) =>
                        (e === "warn" || e === "error" || e === "table")
                ).map((c : any) => jsonify(c, 200, true));
            case "stack-trace":
                // The last global exception thrown
                if (this.handler instanceof GlobalErrorHandler)
                {
                    return jsonify(this.handler.lastError, 200, true);
                }
                return `Custom error handler not present; trace unknown.`;
            case "message-history":
                // Recent websocket messages
                return this.messaging.recentLog.list().map(
                    (m : Message) => jsonify(m, 200, true));
            case "navigation-history":
                // Recent Angular navigation events
                return this.navHistory.getRecent().map(
                    (n : RouterEvent) => jsonify(n, 200, true));
            case "user-name":
                // The name of the currently authenticated user
                return (this.users.self.name || "UNKNOWN");
            case "organization":
                // The organization of the currently authenticated user
                return (this.users.self.org || "UNKNOWN");
            case "user-agent":
                // The user-agent string of the browser
                return navigator.userAgent;
            case "client-language":
                // The language/locale identifiers listed by the browser
                return ((navigator as any).languages[0]
                    || navigator.language);
            case "UTC-offset":
                // The integer number of minutes between this timezone and UTC
                return (new Date()).getTimezoneOffset();
            case "IANA-timezone":
                // The IANA name for the (approximated) timezone of the user
                return moment.tz.guess();
            case "now":
                // Timestamp of the current moment
                return moment().utc().toDate();
            case "today":
                // Timestamp of the beginning of the current day
                return moment().utc().startOf("day").toDate();
            case "window-size":
                // The width, height tuple of the browser's inner dimensions
                return [window.innerHeight, window.innerWidth];
            case "screen-size":
                // The width, height tuple of the screen's dimensions
                return [window.screen.height, window.screen.width];
            case "pixel-ratio":
                // The pixel rendering ratio, for retina / high-density screens
                return window.devicePixelRatio;
            case "geolocation":
                // Currently unimplemented, but eventually a latitude-longitude
                // pair estimating the client's current location.
                throw new Error(`geolocation autofill not yet supported`);
            default:
                throw new Error(`Unknown autofill source "${source}"`);
        }
    }
}

/**
 * Create a provider for easy import that uses the {@link StepTransformer} class
 * as a multi-provider injection token.
 */
export const AUTOFILLER : ClassProvider = {
    provide: StepTransformer,
    useClass: Autofiller,
    multi: true
};



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/pipeline/autofiller.ts